import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Chip,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  CalendarMonth,
  Schedule,
  LocationOn,
  People,
  DirectionsBus,
} from '@mui/icons-material';
import { format } from 'date-fns';
import api from '../../services/api';

interface Schedule {
  id: string;
  shuttle_id: string;
  shuttle_name: string;
  departure_date: string;
  departure_time: string;
  pickup_location: string;
  destination: string;
  available_seats: number;
  total_capacity: number;
  price: number | null;
  club_id: string;
  club_name: string;
}

interface ShuttleBookingFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  userEmail?: string;
  userName?: string;
}

const ShuttleBookingForm: React.FC<ShuttleBookingFormProps> = ({
  open,
  onClose,
  onSuccess,
  userEmail = '',
  userName = '',
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  
  // Form data
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedSchedule, setSelectedSchedule] = useState<string>('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [guestNickname, setGuestNickname] = useState('');
  const [phone, setPhone] = useState('');
  const [seatsBooked, setSeatsBooked] = useState(1);
  const [notes, setNotes] = useState('');
  const [bookingCode, setBookingCode] = useState('');
  const [serviceUserData, setServiceUserData] = useState<any>(null);

  // Dynamic steps based on whether we have user data
  const hasUserData = !!(serviceUserData || userName || userEmail);
  const steps = hasUserData 
    ? ['Select Shuttle', 'Confirmation']
    : ['Select Shuttle', 'Guest Information', 'Confirmation'];

  useEffect(() => {
    if (open) {
      fetchAvailableSchedules();
      // Reset form when opening
      setActiveStep(0);
      setSelectedSchedule('');
      setSeatsBooked(1);
      setNotes('');
      setError(null);
      setSuccess(false);
      setBookingCode('');
      
      // Load service user data if available
      const storedServiceUserData = localStorage.getItem('serviceUserData');
      if (storedServiceUserData) {
        try {
          const userData = JSON.parse(storedServiceUserData);
          setServiceUserData(userData);
          // Pre-fill form with service user data
          setGuestName(userData.name || userName || '');
          setGuestEmail(userData.email || userEmail || '');
          setGuestNickname(userData.nickname || '');
        } catch (error) {
          console.error('Failed to parse service user data:', error);
          // Fall back to props
          setGuestName(userName);
          setGuestEmail(userEmail);
        }
      } else {
        // Use props if no service user data
        setGuestName(userName);
        setGuestEmail(userEmail);
      }
    }
  }, [open, userName, userEmail]);

  const fetchAvailableSchedules = async () => {
    try {
      setLoading(true);
      const response = await api.get('/shuttle/available-schedules');
      // Handle both array response and object with schedules property
      const schedulesData = Array.isArray(response.data) 
        ? response.data 
        : (response.data.schedules || []);
      setSchedules(schedulesData);
    } catch (error: any) {
      setError('Failed to load available shuttles');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && !selectedSchedule) {
      setError('Please select a shuttle schedule');
      return;
    }
    
    // Skip guest info step if we have user data
    if (activeStep === 0 && hasUserData) {
      setError(null);
      setActiveStep(steps.length - 1); // Go directly to confirmation
      return;
    }
    
    if (activeStep === 1 && !hasUserData) {
      if (!guestName || !guestEmail) {
        setError('Please fill in all required fields');
        return;
      }
    }
    setError(null);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const bookingData = {
        schedule_id: selectedSchedule,
        guest_name: guestName,
        guest_email: guestEmail,
        guest_nickname: guestNickname || null,
        phone: phone || null,
        seats_booked: seatsBooked,
        notes: notes || null,
      };

      const response = await api.post('/shuttle/create-booking', bookingData);
      
      if (response.data.booking_code) {
        setBookingCode(response.data.booking_code);
        setSuccess(true);
        
        // Call onSuccess after a delay to show success message
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 2000);
      }
    } catch (error: any) {
      setError(error.response?.data?.detail || 'Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedScheduleDetails = () => {
    return schedules.find(s => s.id === selectedSchedule);
  };

  const renderStepContent = () => {
    // Map activeStep to content based on whether we have user data
    const contentStep = hasUserData && activeStep === 1 ? 2 : activeStep;
    
    switch (contentStep) {
      case 0:
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select an available shuttle schedule
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : schedules.length === 0 ? (
              <Alert severity="info">No shuttles available at this time</Alert>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {schedules.map((schedule) => {
                  const departureDate = new Date(schedule.departure_date);
                  const isSelected = selectedSchedule === schedule.id;
                  
                  return (
                    <Box
                      key={schedule.id}
                      sx={{
                        p: 2,
                        border: 2,
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 2,
                        cursor: 'pointer',
                        backgroundColor: isSelected ? 'primary.50' : 'background.paper',
                        '&:hover': {
                          borderColor: 'primary.main',
                          backgroundColor: 'action.hover',
                        },
                      }}
                      onClick={() => setSelectedSchedule(schedule.id)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          <DirectionsBus sx={{ fontSize: 18, mr: 1, verticalAlign: 'middle' }} />
                          {schedule.shuttle_name}
                        </Typography>
                        <Chip
                          label={`${schedule.available_seats} seats available`}
                          color={schedule.available_seats > 5 ? 'success' : 'warning'}
                          size="small"
                        />
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="body2">
                          <CalendarMonth sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {format(departureDate, 'EEE, MMM d, yyyy')}
                        </Typography>
                        <Typography variant="body2">
                          <Schedule sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                          {schedule.departure_time}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ mt: 1 }}>
                        <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                        {schedule.pickup_location} → {schedule.destination}
                      </Typography>
                      {schedule.price && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Price: €{schedule.price}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
            
            {selectedSchedule && (
              <FormControl fullWidth sx={{ mt: 2 }}>
                <InputLabel>Number of Seats</InputLabel>
                <Select
                  value={seatsBooked}
                  onChange={(e) => setSeatsBooked(Number(e.target.value))}
                  label="Number of Seats"
                >
                  {[...Array(Math.min(getSelectedScheduleDetails()?.available_seats || 1, 4))].map((_, i) => (
                    <MenuItem key={i + 1} value={i + 1}>
                      {i + 1} {i === 0 ? 'seat' : 'seats'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        );

      case 1:
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">
              {serviceUserData || userName || userEmail ? 
                'Please confirm your booking information' : 
                'Please provide your contact information'}
            </Typography>
            <TextField
              fullWidth
              required
              label="Name"
              value={guestName}
              onChange={(e) => setGuestName(e.target.value)}
              disabled={!!serviceUserData?.name}
              helperText={serviceUserData?.name ? 'Name from your account' : ''}
            />
            <TextField
              fullWidth
              required
              label="Email"
              type="email"
              value={guestEmail}
              onChange={(e) => setGuestEmail(e.target.value)}
              disabled={!!serviceUserData?.email}
              helperText={serviceUserData?.email ? 'Email from your account' : ''}
            />
            <TextField
              fullWidth
              label="Nickname (Optional)"
              value={guestNickname}
              onChange={(e) => setGuestNickname(e.target.value)}
              disabled={!!serviceUserData?.nickname}
              helperText={serviceUserData?.nickname ? 'Nickname from your account' : ''}
            />
            <TextField
              fullWidth
              label="Phone (Optional)"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes (Optional)"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              helperText="Any special requirements or information"
            />
          </Box>
        );

      case 2:
        const schedule = getSelectedScheduleDetails();
        if (!schedule) return null;
        
        return (
          <Box>
            {success ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Booking Confirmed!
                </Typography>
                <Typography variant="body2">
                  Booking Code: <strong>{bookingCode}</strong>
                </Typography>
                <Typography variant="body2">
                  A confirmation email has been sent to {guestEmail}
                </Typography>
              </Alert>
            ) : (
              <>
                <Typography variant="subtitle1" gutterBottom>
                  Review Your Booking
                </Typography>
                <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1, mb: 2 }}>
                  <Typography variant="subtitle2" color="primary" gutterBottom>
                    {schedule.shuttle_name}
                  </Typography>
                  <Typography variant="body2">
                    <CalendarMonth sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {format(new Date(schedule.departure_date), 'EEEE, MMMM d, yyyy')}
                  </Typography>
                  <Typography variant="body2">
                    <Schedule sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {schedule.departure_time}
                  </Typography>
                  <Typography variant="body2">
                    <LocationOn sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {schedule.pickup_location} → {schedule.destination}
                  </Typography>
                  <Typography variant="body2">
                    <People sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    {seatsBooked} {seatsBooked === 1 ? 'seat' : 'seats'}
                  </Typography>
                  {schedule.price && (
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Total Price: €{schedule.price * seatsBooked}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ bgcolor: 'background.default', p: 2, borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Guest Information
                  </Typography>
                  <Typography variant="body2">Name: {guestName}</Typography>
                  <Typography variant="body2">Email: {guestEmail}</Typography>
                  {guestNickname && <Typography variant="body2">Nickname: {guestNickname}</Typography>}
                  {phone && <Typography variant="body2">Phone: {phone}</Typography>}
                  {notes && <Typography variant="body2" sx={{ mt: 1 }}>Notes: {notes}</Typography>}
                </Box>
              </>
            )}
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Book a Shuttle
      </DialogTitle>
      <Box sx={{ px: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 2 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {renderStepContent()}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!success && (
          <>
            <Button onClick={onClose}>Cancel</Button>
            {activeStep > 0 && (
              <Button onClick={handleBack} disabled={loading}>
                Back
              </Button>
            )}
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                disabled={loading || (activeStep === 0 && !selectedSchedule)}
              >
                {activeStep === 0 && hasUserData ? 'Review Booking' : 'Next'}
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                Confirm Booking
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ShuttleBookingForm;